import { skipToken, useInfiniteQuery } from '@tanstack/react-query';
import hash from 'object-hash';

import { Clips } from '@air/api';
import { type AssetSearchFiltersCalculated, SortDirection, type SortField } from '@air/api/types';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';

const CLIPS_KEY = 'CLIPS';

const DEFAULT_CLIPS_SORT_FIELD: SortField = { name: 'name', direction: SortDirection.asc };

export const getClipsKey = ({
  workspaceId,
  search = '',
  filters,
  sortField = DEFAULT_CLIPS_SORT_FIELD,
}: {
  workspaceId: string | undefined;
  search?: string;
  filters?: AssetSearchFiltersCalculated;
  sortField?: SortField;
}) => {
  // filters may contain base64 image, so create a hash of the filters to keep the key small
  const filtersHash = filters ? hash(filters) : undefined;

  return [CLIPS_KEY, { workspaceId, search, filtersHash, sortField }];
};

export type UseClipsParams = {
  search?: string;
  filters?: AssetSearchFiltersCalculated;
  limit?: number;
  isSemanticSearch?: boolean;
  sortField?: SortField;
};

export type UseClipsOptions = {
  enabled?: boolean;
};

export const useClips = (
  { search = '', filters, limit, isSemanticSearch = false, sortField = DEFAULT_CLIPS_SORT_FIELD }: UseClipsParams,
  options?: UseClipsOptions,
) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  return useInfiniteQuery({
    queryKey: getClipsKey({ search, workspaceId, filters, sortField }),
    queryFn: workspaceId
      ? async ({ pageParam }) => {
          return Clips.search({
            workspaceId,
            search,
            filters,
            limit,
            semantic: isSemanticSearch,
            cursor: pageParam || undefined,
            sortField,
          });
        }
      : skipToken,
    enabled: !!workspaceId && (options?.enabled ?? true),
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor,
    initialPageParam: '',
  });
};
